import type { FulfillmentOrderLine } from "../types";
import type { EntryExistingDraft, EntryParseBaseInput, EntryParseInput, EntryParseResult } from "./confirmationEntryTypes";
import { changedEntryFields } from "./confirmationEntryChange";
import { isUnchangedEntrySource } from "./confirmationEntryIdentity";

export type ExistingConfirmationEntry = { base: FulfillmentOrderLine; input: EntryParseInput; baseline: Partial<FulfillmentOrderLine> | null };
export type ExistingEntryParseResult = { patch: Partial<FulfillmentOrderLine>; errors: EntryParseResult["errors"] };

/** 同名原文只有整组保持或原片段未改时才可复用，避免改价后抢占另一原行。 */
export function createExistingEntrySourceMatcher(
  seeds: ExistingConfirmationEntry[], text: string, identity: (line: Partial<FulfillmentOrderLine>) => string,
  normalize: (value: string) => string = value => value.trim(),
) {
  const originalText = seeds.map(record => record.input.text).join("\n");
  const groups = new Map<string, Map<string, number>>();
  const counts = new Map<string, number>();
  for (const match of text.matchAll(/[^\n;；]+/g)) {
    const raw = normalize(match[0]);
    if (raw) counts.set(raw, (counts.get(raw) || 0) + 1);
  }
  let offset = 0;
  const records = seeds.map(seed => {
    const key = identity(seed.baseline || seed.base), raw = normalize(seed.input.text);
    const group = groups.get(key) || new Map<string, number>();
    group.set(raw, (group.get(raw) || 0) + 1); groups.set(key, group);
    const record = { identity: key, text: originalText, sourceStart: offset, sourceEnd: offset + seed.input.text.length };
    offset = record.sourceEnd + 1;
    return record;
  });
  const unchanged = new Set([...groups].filter(([, group]) => [...group].every(([raw, count]) => counts.get(raw) === count)).map(([key]) => key));
  return (index: number, source: Pick<EntryParseResult["items"][number], "sourceStart" | "sourceEnd">): boolean =>
    unchanged.has(records[index].identity) || isUnchangedEntrySource(records[index], source, text);
}

/** 回填文本按原文和唯一业务身份对应原行，禁止靠行序把量价修改转成新增费用。 */
export function parseSelectedEntryDraft(
  input: EntryParseBaseInput,
  seeds: ExistingConfirmationEntry[],
  parse: (input: EntryParseBaseInput) => EntryParseResult,
  parseExisting: (seed: ExistingConfirmationEntry, input: EntryParseBaseInput) => ExistingEntryParseResult,
  identity: (line: Partial<FulfillmentOrderLine>) => string,
): EntryParseResult & { drafts: EntryExistingDraft[]; locations: Record<string, { sourceStart: number; sourceEnd: number }> } {
  const drafts = seeds.map(({ base }) => ({ base, patch: {} as Partial<FulfillmentOrderLine>, removed: false }));
  const result: EntryParseResult = { items: [], errors: [] };
  const sources = [...input.text.matchAll(/[^\n;；]+/g)].filter(source => source[0].trim());
  const used = new Set<number>();
  const matches = new Map<number, number>();
  const canMatchSource = createExistingEntrySourceMatcher(seeds, input.text, identity);
  const locations: Record<string, { sourceStart: number; sourceEnd: number }> = {};
  for (const [index, source] of sources.entries()) {
    const exact = seeds.findIndex((seed, i) => !used.has(i) && seed.input.text.trim() === source[0].trim()
      && canMatchSource(i, { sourceStart: source.index, sourceEnd: source.index + source[0].length }));
    if (exact >= 0) { matches.set(index, exact); used.add(exact); }
  }
  const pending: Array<{ index: number; item: EntryParseResult["items"][number] }> = [];
  for (const [index, source] of sources.entries()) {
    if (matches.has(index)) continue;
    // 日期属于各原行；匹配时只读取计费身份，真正的新增仍按当前录入上下文校验。
    const parsed = parse({ ...input, range: "", text: source[0], allowZeroQuantity: seeds.some(seed => seed.input.allowZeroQuantity) });
    const location = { sourceStart: source.index, sourceEnd: source.index + source[0].length };
    result.errors.push(...parsed.errors.map(error => ({ ...error, ...location })));
    pending.push(...parsed.items.map(item => ({ index, item: { ...item, ...location, key: `${index}-0` } })));
  }
  for (const { index, item } of pending) {
    const candidates = seeds.map((seed, i) => ({ seed, i })).filter(({ seed, i }) => !used.has(i) && identity(seed.baseline || seed.base) === identity(item.patch));
    const duplicates = pending.filter(other => identity(other.item.patch) === identity(item.patch));
    const collidingText = sources.filter(source => source[0].trim() === sources[index][0].trim()).length > 1;
    if (candidates.length && (candidates.length !== 1 || duplicates.length !== 1 || collidingText || candidates.some(({ seed }) => seeds.filter(other => other.input.text === seed.input.text).length > 1))) {
      result.errors.push({ ...item, field: "formula", message: "所选同名费用无法唯一对应原行，请逐条修改，避免覆盖其他费用" });
      continue;
    }
    if (candidates.length) { matches.set(index, candidates[0].i); used.add(candidates[0].i); }
    else {
      const parsed = parse({ ...input, text: sources[index][0], allowZeroQuantity: false });
      result.errors.push(...parsed.errors.map(error => ({ ...error, sourceStart: item.sourceStart, sourceEnd: item.sourceEnd })));
      result.items.push(...parsed.items.map(entry => ({ ...entry, key: item.key, sourceStart: item.sourceStart, sourceEnd: item.sourceEnd })));
    }
  }
  for (const [index, seedIndex] of matches) {
    const source = sources[index], seed = seeds[seedIndex];
    locations[seed.base.id] = { sourceStart: source.index, sourceEnd: source.index + source[0].length };
    if (source[0].trim() === seed.input.text.trim()) continue;
    const parsed = parseExisting(seed, { ...seed.input, routeDays: input.routeDays, roleCounts: input.roleCounts, text: source[0] });
    drafts[seedIndex].patch = parsed.patch;
    result.errors.push(...parsed.errors.map(error => ({ ...error, sourceStart: source.index, sourceEnd: source.index + source[0].length })));
  }
  if ((result.items.length && seeds.some((_, i) => !used.has(i))) || seeds.some((seed, i) => !used.has(i) && seeds.some((other, j) => used.has(j) && other.input.text === seed.input.text))) {
    result.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "formula", message: "原费用无法唯一对应，请逐条修改或明确移除原行后再新增" });
  }
  if (!result.errors.length) drafts.forEach((draft, i) => { draft.removed = !used.has(i); });
  return { ...result, drafts, locations };
}

/** 原行回编只允许一个解析结果，拒绝把原 ID 隐式拆成新增行。 */
export function singleExistingEntryPatch(result: EntryParseResult, text: string): ExistingEntryParseResult {
  if (result.errors.length) return { patch: {}, errors: result.errors };
  if (result.items.length !== 1) return { patch: {}, errors: [{ message: "编辑原明细每次只能保留一条费用，请勿拆分为多条；新增费用请使用分类新增入口", raw: text, sourceStart: 0, sourceEnd: text.length, field: "formula" }] };
  return { patch: result.items[0].patch, errors: [] };
}

/** 仅比较分类解析后的基线与目标值，保护归口身份及未表达的历史字段。 */
export function diffExistingEntry(seed: ExistingConfirmationEntry, parsed: Partial<FulfillmentOrderLine>, baseline: Partial<FulfillmentOrderLine> | null, text: string, { repairedDates = false, resourceChanged = false, resourceSpec = false }: { repairedDates?: boolean; resourceChanged?: boolean; resourceSpec?: boolean }): ExistingEntryParseResult {
  if (!baseline) return { patch: {}, errors: [{ message: "原明细的计费或资源信息无法完整转换为当前录入语法，原值已保留；请先核对原明细", raw: text, sourceStart: 0, sourceEnd: text.length, field: "formula" }] };
  const fields: Array<keyof FulfillmentOrderLine> = ["itemNameSnapshot", "itemSpecSnapshot", "quantity", "billingCount", "unitPriceAmount", "unit", "travelerRole", "mealType", "resourceSelection", "dayExpression", "dayNumbers", "serviceDates", "serviceDateFrom", "serviceDateTo", "dateMappingStatus", "fulfillmentSupplierId", "fulfillmentSupplierName", "providerAssignmentStatus", "settlementSupplierId", "settlementSupplierName"];
  const patch = Object.fromEntries(fields.filter(field => JSON.stringify(parsed[field]) !== JSON.stringify(baseline[field])).map(field => [field, parsed[field]])) as Partial<FulfillmentOrderLine>;
  if (repairedDates) Object.assign(patch, Object.fromEntries(["dayExpression", "dayNumbers", "serviceDates", "serviceDateFrom", "serviceDateTo", "dateMappingStatus"].map(field => [field, parsed[field as keyof FulfillmentOrderLine]])));
  if (!seed.baseline && resourceChanged && JSON.stringify(parsed.resourceSelection) !== JSON.stringify(seed.base.resourceSelection)) {
    patch.resourceSelection = parsed.resourceSelection;
    patch.itemNameSnapshot = parsed.itemNameSnapshot;
    if (resourceSpec) patch.itemSpecSnapshot = parsed.itemSpecSnapshot;
  }
  if (seed.base.fulfillmentOrderId) { delete patch.settlementSupplierId; delete patch.settlementSupplierName; }
  if (patch.resourceSelection) Object.assign(patch, { spuId: null, skuId: null, spuTableName: null, skuTableName: null, spuNameSnapshot: null, skuNameSnapshot: null });
  return { patch: changedEntryFields({ base: seed.base, patch, removed: false }), errors: [] };
}
